import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import { MapPin, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOUSE_IMG = "https://images.unsplash.com/photo-1770625467384-304e461ef1be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob21lJTIwdGhlcm1vc3RhdHxlbnwwfHx8fDE3NzgyMTM4ODN8MA&ixlib=rb-4.1.0&q=85";

const FALLBACK = {
  DC: ["NW", "NE", "SW", "SE"],
  MD: ["Silver Spring", "Rockville", "Gaithersburg", "Bowie", "Bethesda", "Hyattsville"],
  VA: ["Arlington", "Alexandria", "Fairfax", "Woodbridge", "Falls Church", "Reston"],
};

export const ServiceAreas = () => {
  const { t } = useLang();
  const [areas, setAreas] = useState(FALLBACK);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/service-areas`)
      .then((r) => alive && r.data && setAreas(r.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const cards = [
    { key: "DC", label: t.areas.dc, list: areas.DC || [], accent: "cool" },
    { key: "MD", label: t.areas.md, list: areas.MD || [], accent: "warm" },
    { key: "VA", label: t.areas.va, list: areas.VA || [], accent: "cool" },
  ];

  return (
    <section id="areas" data-testid="areas-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-heat">{t.areas.eyebrow}</span>
            <h2 className="mt-4 font-heading font-bold text-white tracking-tight text-4xl sm:text-5xl leading-[1.05]" data-testid="areas-title">
              {t.areas.title}
            </h2>
            <p className="mt-4 text-white/70">{t.areas.subtitle}</p>
          </div>
          <div className="hidden lg:block w-72 h-44 rounded-3xl overflow-hidden border border-white/10 relative">
            <img src={HOUSE_IMG} alt="DMV homes" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-ink/85 via-ink/40 to-transparent" />
            <div className="absolute bottom-3 left-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-ink-card/80 backdrop-blur border border-white/10 text-xs">
              <MapPin size={14} className="text-cool" /> DMV Metro
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {cards.map((c) => {
            const isCool = c.accent === "cool";
            return (
              <div
                key={c.key}
                data-testid={`area-card-${c.key}`}
                className={`rounded-3xl bg-ink-card border border-white/10 p-7 transition ${isCool ? "card-cool" : "card-warm"}`}
              >
                <div className="flex items-center justify-between">
                  <div className={`text-3xl font-heading font-extrabold tracking-tight ${isCool ? "text-cool" : "text-heat"}`}>{c.key}</div>
                  <span className="text-xs uppercase tracking-widest text-white/55">{c.list.length} zones</span>
                </div>
                <h3 className="mt-2 font-heading font-bold text-white text-xl">{c.label}</h3>
                <ul className="mt-5 grid grid-cols-2 gap-y-2 gap-x-3">
                  {c.list.map((z) => (
                    <li key={z} className="flex items-center gap-2 text-sm text-white/80">
                      <ChevronRight size={14} className={isCool ? "text-cool" : "text-heat"} />
                      {z}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
